<template>
  <div class="max-w-5xl mx-auto">
    <!-- Success Message Banner -->
    <div v-if="showSuccessMessage" class="mb-6 p-4 bg-green-900/50 border border-green-600 rounded-lg flex items-center gap-3">
      <span class="material-icons text-green-400">check_circle</span>
      <p class="text-green-300 flex-1">{{ $t('billing.paymentSuccess') }}</p>
      <Button
        id="btn-billing-dismiss-success"
        text
        rounded
        @click="showSuccessMessage = false"
        class="ml-auto text-green-400 hover:text-green-300"
        :aria-label="$t('common.close')"
      >
        <span class="material-icons text-sm select-none">close</span>
      </Button>
    </div>

    <!-- Header -->
    <h1 class="text-3xl font-bold text-white mb-6">{{ $t('billing.title') }}</h1>

    <!-- Back Button -->
    <BackButton />

    <!-- Loading State -->
    <div v-if="loading && !summary" class="bg-gray-800 rounded-lg p-12 text-center">
      <div class="flex justify-center mb-4">
        <ProgressSpinner
          style="width: 3rem; height: 3rem"
          stroke-width="3"
          :aria-label="$t('common.loading')"
        />
      </div>
      <p class="text-gray-400">{{ $t('common.loading') }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error && !summary" class="bg-red-900/50 border border-red-700 rounded-lg p-8 text-center">
      <span class="material-icons text-4xl text-red-400 mb-4">error_outline</span>
      <p class="text-red-300 mb-4">{{ error }}</p>
      <Button
        id="btn-billing-retry"
        severity="danger"
        @click="fetchSummary"
        :label="`${$t('common.error')} - ${$t('billing.retryButton')}`"
      />
    </div>

    <!-- Billing Active State -->
    <div v-else-if="!loading && !error" class="space-y-6">
      <!-- Balance and Monthly Spending Cards -->
      <div class="grid gap-6" :class="autoRechargeEnabled ? 'md:grid-cols-2' : 'grid-cols-1'">
        <!-- Balance Card -->
        <div class="bg-gray-800 rounded-lg p-6">
          <!-- Header with integrated purchase button -->
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-3">
              <span class="material-icons text-2xl text-green-400">account_balance_wallet</span>
              <h2 class="text-lg font-semibold text-white">{{ $t('billing.balance.title') }}</h2>
            </div>
            <!-- Integrated purchase button -->
            <div class="flex flex-col items-end gap-1">
              <div class="flex gap-2">
                <!-- Se c'è una carta predefinita, usa quella come principale -->
                <!-- Label + #icon, not default-slot content: PrimeVue only swaps the
                     icon slot for its spinner while loading, so content in the
                     default slot would hide the spinner entirely. -->
                <Button
                  id="btn-billing-pay-saved-card"
                  v-if="hasDefaultCard"
                  :label="$t('billing.payWithDefaultCard', { amount: RICARICA_SCALINO_EUR })"
                  @click="handleQuickPurchaseWithSavedCard"
                  :loading="isRedirecting"
                  :disabled="isRedirecting"
                >
                  <template #icon>
                    <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">credit_card</span>
                  </template>
                </Button>
                <!-- Se NON c'è una carta predefinita, usa il checkout standard -->
                <Button
                  id="btn-billing-buy-tokens"
                  v-else
                  :label="isRedirecting ? $t('billing.redirecting') : $t('billing.buyTokens', { amount: RICARICA_SCALINO_EUR })"
                  @click="handleQuickPurchase"
                  :loading="isRedirecting"
                  :disabled="isRedirecting"
                >
                  <template #icon>
                    <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">add</span>
                  </template>
                </Button>
              </div>
              <!-- Messaggio carta predefinita -->
              <span v-if="hasDefaultCard" class="text-xs text-gray-500">
                {{ $t('billing.defaultCardInfo', { brand: paymentMethods?.find(p => p.isDefault)?.brand || 'card', last4: paymentMethods?.find(p => p.isDefault)?.last4 || '****' }) }}
              </span>
            </div>
          </div>
          <div class="text-3xl font-bold text-white mb-4">
            {{ toEuro(totalTokens) }}
            <span class="text-sm font-normal text-gray-400">{{ $t('billing.balance.remaining') }}</span>
          </div>
          
          <!-- Gestione carte - Link al Customer Portal -->
          <div class="bg-gray-900/50 border border-gray-700 rounded-lg p-3 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="material-icons text-gray-400 text-lg">receipt_long</span>
              <span class="text-gray-400 text-sm">{{ $t('billing.managePaymentMethods.title') }}</span>
            </div>
            <Button
              id="btn-billing-open-stripe-portal"
              text
              rounded
              @click="openStripePortal"
              class="text-cyan-400 hover:text-cyan-300"
              v-tooltip.bottom="$t('billing.managePaymentMethods.openStripe')"
              :aria-label="$t('billing.managePaymentMethods.openStripe')"
            >
              <span class="material-icons text-lg select-none" aria-hidden="true">open_in_new</span>
            </Button>
          </div>
        </div>

        <!-- Monthly Spending Card (visible only when auto-recharge is enabled) -->
        <div v-if="autoRechargeEnabled" class="bg-gray-800 rounded-lg p-6">
          <div class="flex items-center gap-3 mb-4">
            <span class="material-icons text-2xl text-blue-400">calendar_today</span>
            <h2 class="text-lg font-semibold text-white">{{ $t('billing.thisMonth.title') }}</h2>
          </div>
          <div class="text-3xl font-bold text-white mb-2">
            €{{ monthlySpending.spent.toFixed(2) }}
          </div>
          <p class="text-gray-400 text-sm mb-3">
            {{ $t('billing.thisMonth.spentOf') }} €{{ monthlySpending.cap.toFixed(2) }} {{ $t('billing.thisMonth.cap') }}
          </p>
          <ProgressBar 
            :value="monthlySpending.percentage" 
            :showValue="true"
            class="h-3"
          />
        </div>
      </div>

      <!-- Auto-Recharge Section -->
      <div class="bg-gray-800 rounded-lg p-6">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-3">
            <span class="material-icons text-2xl text-yellow-400">bolt</span>
            <h2 class="text-lg font-semibold text-white">{{ $t('billing.autoRecharge.title') }}</h2>
          </div>
          <ToggleSwitch
            id="btn-billing-toggle-auto-recharge"
            :model-value="autoRechargeEnabled"
            @update:model-value="toggleAutoRecharge"
            :disabled="savingAutoRecharge || !autoRechargeConfig?.hasStripeCustomer"
          />
        </div>

        <p class="text-gray-400 text-sm" :class="autoRechargeConfig?.hasStripeCustomer ? 'mb-4' : 'mb-2'">
          {{ $t('billing.autoRecharge.description') }}
        </p>
        <p v-if="!autoRechargeConfig?.hasStripeCustomer" class="text-yellow-500 text-xs mb-4 flex items-center gap-1">
          <span class="material-icons text-sm">info</span>
          {{ $t('billing.autoRecharge.requiresPurchase') }}
        </p>

        <!-- Auto-Recharge Settings (shown when enabled) -->
        <div v-if="autoRechargeEnabled" class="space-y-4">
          <!-- Cap mensile e calcolo tokens -->
          <div class="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
            <label class="block text-gray-300 text-sm font-medium mb-3">
              {{ $t('billing.autoRecharge.capDescription') }}
            </label>
            <CapInput 
              v-model="autoRechargeForm.cap"
              :min="RICARICA_SCALINO_EUR"
              :max="1000"
              :step="RICARICA_SCALINO_EUR"
              class="w-full mb-3"
            />
            
            <!-- Info ricariche e tokens stimati -->
            <div class="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
              <div class="flex items-center gap-2">
                <span class="material-icons text-cyan-400 text-lg">bolt</span>
                <span class="text-gray-400">
                  {{ $t('billing.autoRecharge.maximum') }} <span class="text-cyan-400 font-semibold">{{ maxMonthlyRechargesDisplay }}</span> {{ $t('billing.autoRecharge.rechargesPerMonth') }}
                </span>
              </div>
              <span class="text-gray-600 hidden sm:inline">•</span>
              <div class="flex items-center gap-2">
                <span class="text-gray-400">
                  <span class="text-cyan-400 font-semibold">{{ RICARICA_SCALINO_EUR }}€</span> {{ $t('billing.autoRecharge.each') }}
                </span>
              </div>
              <span class="text-gray-600 hidden sm:inline">•</span>
              <div class="flex items-center gap-2">
                <span class="material-icons text-green-400 text-lg">toll</span>
                <span class="text-gray-400">
                  <span class="text-green-400 font-semibold">~{{ formatNumber(estimatedTokensDisplay) }}</span> {{ $t('billing.tokens') }} {{ $t('billing.autoRecharge.estimated') }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex justify-end">
            <Button
              id="btn-billing-save-auto-recharge"
              @click="handleSaveAutoRecharge"
              :loading="savingAutoRecharge"
              :disabled="savingAutoRecharge"
              :label="savingAutoRecharge ? $t('billing.autoRecharge.saving') : $t('common.save')"
            />
          </div>
        </div>
      </div>

      <!-- Purchase History Section -->
      <div class="bg-gray-800 rounded-lg p-6">
        <div class="flex items-center gap-3 mb-4">
          <span class="material-icons text-2xl text-purple-400">receipt_long</span>
          <h2 class="text-lg font-semibold text-white">{{ $t('billing.purchaseHistory.title') }}</h2>
        </div>

        <div v-if="purchases.length === 0" class="text-center py-8 text-gray-400">
          <span class="material-icons text-4xl mb-2">shopping_cart</span>
          <p>{{ $t('billing.purchaseHistory.empty') }}</p>
        </div>

        <DataTable 
          v-else
          :value="purchases" 
          :paginator="purchases.length > 10" 
          :rows="10"
          :rowHover="true"
        >
          <Column :header="$t('billing.purchaseHistory.date')">
            <template #body="{ data }">
              {{ formatDate(data.createdAt) }}
            </template>
          </Column>
          <Column :header="$t('billing.purchaseHistory.tokens')">
            <template #body="{ data }">
              <span class="text-green-400">+{{ formatNumber(data.tokensAmount) }}</span>
            </template>
          </Column>
          <Column :header="$t('billing.purchaseHistory.amount')">
            <template #body="{ data }">
              €{{ data.amountEur.toFixed(2) }}
            </template>
          </Column>
          <Column :header="$t('billing.purchaseHistory.status')">
            <template #body="{ data }">
              <span 
                class="flex items-center gap-1"
                :class="data.status === 'completed' ? 'text-green-400' : 'text-yellow-400'"
              >
                <span v-if="data.status === 'completed'" class="material-icons text-sm">check_circle</span>
                <span v-else class="material-icons text-sm">schedule</span>
                {{ $t(`billing.purchaseHistory.statuses.${data.status}`) }}
              </span>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useBilling } from '../composables/useBilling';
import { toEuro } from '../composables/useTokenStore';
import CapInput from '../components/billing/CapInput.vue';
import BackButton from '../components/ui/BackButton.vue';

// I18n
const { t } = useI18n();

// Route
const route = useRoute();
const workspaceId = computed(() => route.params.workspaceId);

// Billing composable
const {
  summary,
  autoRechargeConfig,
  purchases,
  loading,
  error,
  savingAutoRecharge,
  totalTokens,
  totalAllowance,
  tokenUsed,
  monthlySpending,
  autoRechargeEnabled,
  tokenMultiplier,
  maxMonthlyRecharges,
  estimatedTokensPerMonth,
  RICARICA_SCALINO_EUR,
  fetchSummary,
  fetchPurchases,
  saveAutoRecharge,
  createCheckoutSession,
  openStripePortal,
  fetchAutoRechargeConfig,
  payWithSavedCard,
  hasDefaultCard,
  paymentMethods,
} = useBilling(workspaceId, t);

// Success message state
const showSuccessMessage = ref(false);

// Redirect state for quick purchase
const isRedirecting = ref(false);

// Form state
const autoRechargeForm = reactive({
  cap: 100
});

// Computed per calcolo in tempo reale del massimo ricariche mensili (basato sul form locale)
const maxMonthlyRechargesDisplay = computed(() => {
  return Math.floor(autoRechargeForm.cap / RICARICA_SCALINO_EUR);
});

// Computed per tokens stimati basato sul cap del form locale
const estimatedTokensDisplay = computed(() => {
  const cap = autoRechargeForm.cap || 0;
  if (!cap || cap < RICARICA_SCALINO_EUR) return 0;
  const numRecharges = Math.floor(cap / RICARICA_SCALINO_EUR);
  const totalEur = numRecharges * RICARICA_SCALINO_EUR;
  // Usa tokenMultiplier dalla config, arrotondato a 2 decimali
  const multiplier = Math.round((tokenMultiplier.value || 5_000_000) * 100) / 100;
  return Math.floor(totalEur * multiplier);
});

// Watch auto-recharge config changes
watch(autoRechargeConfig, (newConfig) => {
  if (newConfig) {
    // Ensure cap is always a multiple of 20
    const rawCap = newConfig.monthlyCapEur || 100;
    autoRechargeForm.cap = Math.round(rawCap / RICARICA_SCALINO_EUR) * RICARICA_SCALINO_EUR;
  }
}, { immediate: true });

// Toggle auto-recharge
const toggleAutoRecharge = async () => {
  const newState = !autoRechargeEnabled.value;
  const result = await saveAutoRecharge({ enabled: newState });
  if (!result.success && result.error) {
    // Error is handled by composable
  }
};

// Save auto-recharge settings
const handleSaveAutoRecharge = async () => {
  const result = await saveAutoRecharge({
    enabled: true,
    monthlyCapEur: autoRechargeForm.cap
  });
  if (!result.success && result.error) {
    // Error is handled by composable
  }
};

// Quick purchase - Direct Stripe redirect for €20
const handleQuickPurchase = async () => {
  isRedirecting.value = true;
  
  // Direct redirect to Stripe Checkout for €20
  await createCheckoutSession(RICARICA_SCALINO_EUR, false);
  
  // Note: The page will redirect, so isRedirecting won't reset unless there's an error
  // If we get here, the redirect failed
  isRedirecting.value = false;
};

// Quick purchase with saved card - Direct payment without redirect
const handleQuickPurchaseWithSavedCard = async () => {
  isRedirecting.value = true;
  
  const result = await payWithSavedCard(RICARICA_SCALINO_EUR);
  
  if (result.success && result.completed) {
    // Payment completed successfully, show success message
    showSuccessMessage.value = true;
    setTimeout(async() => {
      showSuccessMessage.value = false;
      // Refresh dati per mostrare i token acquistati
      await fetchSummary();
      await fetchPurchases();
    }, 5000);
  } else if (!result.success) {
    // Show error
    error.value = result.error;
  }
  
  isRedirecting.value = false;
};

// Format number with K suffix
const formatNumber = (num) => {
  return num.toLocaleString('it-IT');
};

// Format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

// Check for Stripe redirect on mount
onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  
  if (urlParams.get('success') === 'true' || urlParams.has('session_id')) {
    // Refetch data to update billing status
    fetchSummary();
    fetchPurchases();
    
    // Remove query params from URL
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);
    
    // Set success message
    showSuccessMessage.value = true;
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      showSuccessMessage.value = false;
    }, 5000);
  }
});
</script>

<style scoped>
:deep(.p-progressbar) {
  background: #374151;
  border-radius: 9999px;
}

:deep(.p-progressbar .p-progressbar-value) {
  background: linear-gradient(90deg, #06b6d4, #10b981);
  border-radius: 9999px;
}
</style>
